<template>
  <div class="tenant-customers-wrap">
    <div class="section-header">
      <div class="section-header__left">
        <div class="section-header__icon">
          <i class="el-icon-office-building"></i>
        </div>
        <div>
          <h2 class="section-header__title">{{ $t('settings.tenantCustomers') }}</h2>
          <p class="section-header__subtitle">{{ filteredCustomers.length }} {{ $t('settings.entries') }}</p>
        </div>
      </div>

      <div class="section-header__actions">
        <base-button
          type="primary"
          icon="el-icon-plus"
          @click="$router.push({path: `/app/tenants/${$route.params.id}/customers/create`})">
          {{ $t('settings.createCustomer') }}
        </base-button>
        <base-button
          icon="el-icon-refresh"
          :loading="loading"
          @click="loadCustomers">
          {{ $t('settings.refresh') }}
        </base-button>
        <base-button
          icon="el-icon-back"
          @click="$router.push({path: '/app/tenants'})">
          {{ $t('settings.backToTenants') }}
        </base-button>
      </div>
    </div>

    <div class="search-container">
      <el-input
        class="table-search"
        v-model="searchTerm"
        :placeholder="$t('settings.customer.search')"
        prefix-icon="el-icon-search"
        clearable>
      </el-input>
      <div class="search-hint">{{ $t('settings.searchHint') }}</div>
    </div>

    <div class="table-card">
      <base-table
        :loading="loading"
        :data="filteredCustomers"
        :empty-text="$t('settings.customersTableEmpty')"
        class="connections-table"
        style="width: 100%">

        <el-table-column prop="name" min-width="180" :label="$t('settings.name')">
          <template slot-scope="scope">
            <span class="cell-bold">{{ scope.row.name }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="appLoginDomain" min-width="160" :label="$t('settings.appLoginDomain')">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.appLoginDomain" type="info" size="mini" class="domain-tag">
              {{ scope.row.appLoginDomain }}
            </el-tag>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column prop="mail" min-width="220" :label="$t('settings.mail')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.mail">
              <i class="el-icon-message cell-url__icon"></i>
              <span>{{ scope.row.mail }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column prop="default" align="center" width="150" :label="$t('settings.defaultCustomer')">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.default"
              @click.native.stop
              @change="updateDefaultStatus(scope.row)">
            </el-switch>
          </template>
        </el-table-column>

        <el-table-column align="center" fixed="right" width="180" :label="$t('settings.operations')">
          <template slot-scope="customer">
            <div class="action-cell">
              <el-tooltip :content="$t('settings.edit')" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-edit"
                  size="mini"
                  circle
                  @click.stop.prevent="onCustomerEdit(customer.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.showCustomerAccounts')" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-s-custom"
                  size="mini"
                  circle
                  @click.stop.prevent="viewCustomerAccounts(customer.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.syncCustomerAccounts')" placement="top">
                <el-button
                  :disabled="!canSync(customer.row)"
                  type="primary"
                  icon="el-icon-refresh"
                  size="mini"
                  circle
                  :loading="customer.row.loading"
                  @click.stop.prevent="syncCustomerAccounts(customer.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.deleteCustomer')" placement="top">
                <el-button
                  type="danger"
                  icon="el-icon-delete"
                  size="mini"
                  circle
                  @click.stop.prevent="onCustomerDelete(customer.row)">
                </el-button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </base-table>
    </div>

    <base-delete-dialog
      :visible.sync="deleteCustomerDialog"
      :title="$t('settings.deleteCustomer')"
      :text="$t('settings.deleteCustomerInfoText')"
      :confirm-label="$t('settings.deleteCustomer')"
      :loading="loading"
      @confirm="confirmDeleteCustomer">
    </base-delete-dialog>
  </div>
</template>
<script>
import BaseButton from '@/components/common/BaseButton';
import {createNamespacedHelpers} from 'vuex';
import BaseTable from '@/components/common/BaseTable';
import BaseDeleteDialog from '@/components/common/BaseDeleteDialog';

const tenantStateMappers = createNamespacedHelpers('tenant');

export default {
  name: "TenantCustomers",
  components: {
    BaseTable,
    BaseDeleteDialog,
    BaseButton
  },
  data() {
    return {
      searchTerm: '',
      loading: false,
      deleteCustomerDialog: false,
      customerAccountsDialog: false,
      selectedCustomer: null,
      syncingCustomerId: null
    }
  },
  computed: {
    ...tenantStateMappers.mapGetters({customers: 'getTenantCustomers'}),
    filteredCustomers() {
      if (!this.searchTerm) {
        return this.customers;
      }

      const searchLower = this.searchTerm.toLowerCase();
      return this.customers.filter(customer => {
        return (
          (customer.name && customer.name.toLowerCase().includes(searchLower)) ||
          (customer.appLoginDomain && customer.appLoginDomain.toLowerCase().includes(searchLower)) ||
          (customer.mail && customer.mail.toLowerCase().includes(searchLower))
        );
      });
    }
  },
  mounted() {
    this.loadCustomers();
  },
  methods: {
    ...tenantStateMappers.mapActions(['getCustomersByTenant', 'deleteCustomer']),
    loadCustomers() {
      this.loading = true;
      this.getCustomersByTenant(this.$route.params.id)
        .finally(() => {
          this.loading = false;
        });
    },
    canSync(customer) {
      return customer.remoteAccountProviderCount > 0;
    },
    onCustomerEdit(customer) {
      this.$router.push({path: `/app/tenants/${this.$route.params.id}/customers/${customer.id}/edit`});
    },
    viewCustomerAccounts(customer) {
      this.$router.push({
        path: `/app/tenants/${this.$route.params.id}/customers/${customer.id}/accounts`
      });
    },
    syncCustomerAccounts(customer) {
      customer.loading = true;
      // Import customerService and use syncAccounts method
      import('@/services/CustomerService.class').then(({customerService}) => {
        customerService.syncAccounts(customer.id)
          .then(() => {
            this.$message({
              message: `${customer.name} ${this.$t('settings.showCustomerAccountsSuccess')}`,
              type: 'success'
            });
          }).catch(() => {
          this.$message({
            message: `${customer.name} ${this.$t('settings.showCustomerAccountsError')}`,
            type: 'error'
          });
        }).finally(() => {
          customer.loading = false;
          this.loadCustomers();
        });
      });
    },
    onCustomerDelete(customer) {
      this.selectedCustomer = customer;
      this.deleteCustomerDialog = true;
    },
    confirmDeleteCustomer() {
      if (this.selectedCustomer) {
        this.loading = true;
        this.deleteCustomer({
          tenantId: this.$route.params.id,
          customerId: this.selectedCustomer.id
        }).then(() => {
          this.$message({
            type: 'success',
            message: this.$t('settings.customerDeleted')
          });
          this.deleteCustomerDialog = false;
          this.selectedCustomer = null;
        }).catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
        }).finally(() => {
          this.loading = false;
        });
      }
    },
    updateDefaultStatus(customer) {
      // Import customerService and patch the customer
      import('@/services/CustomerService.class').then(({customerService}) => {
        this.loading = true;
        customerService.patch(customer)
          .then(() => {
            this.$message.success(this.$t('settings.patchSuccess'));
            this.loadCustomers();
          })
          .catch(() => {
            this.$message.error(this.$t('settings.genericHttpError'));
            // Revert the switch state if there was an error
            customer.default = !customer.default;
          })
          .finally(() => {
            this.loading = false;
          });
      });
    }
  }
}
</script>

<style scoped lang="scss">
.tenant-customers-wrap {
  min-width: 0;
  padding: 0 15px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: var(--bg-surface);
      font-size: 18px;
    }
  }

  &__title {
    margin: 0 0 2px;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__subtitle {
    margin: 0;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.search-container {
  margin-bottom: 16px;

  .table-search {
    max-width: 400px;
    margin-bottom: 5px;
  }

  .search-hint {
    font-size: 12px;
    color: var(--text-secondary);
    margin-left: 5px;
  }
}

.table-card {
  background: var(--bg-surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.cell-bold {
  font-weight: 600;
  font-size: 13.5px;
  color: var(--text-primary);
}

.cell-url {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 13px;

  &__icon {
    color: var(--text-muted);
    font-size: 14px;
    flex-shrink: 0;
  }
}

.cell-empty {
  color: var(--text-muted);
}

.domain-tag {
  font-weight: 500;
  border-radius: 6px;
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

@media (max-width: 767px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;

    &__actions {
      flex-wrap: wrap;
    }
  }
}
</style>
